"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Icon } from "../icon";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
import type { SelectProps } from "./select.types";

/** Lists longer than this become type-to-filter by default. */
const SEARCH_THRESHOLD = 8;

/**
 * Select Component
 *
 * V&A Waterfront Design System - Custom Dropdown Select
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=11961-20801
 *
 * Custom dropdown matching the design system. Replaces native `<select>` with a
 * styled trigger and a scrollable options list. For long lists the trigger
 * becomes a combobox you can type into to filter the options inline.
 */
const Select = React.forwardRef<HTMLDivElement, SelectProps>((t0, ref) => {
  const $ = _c(147);
  const {
    id: idProp,
    name,
    label,
    placeholder: t1,
    helperText,
    errorText,
    onDark: t2,
    options: t3,
    value,
    onChange,
    onBlur,
    disabled: t4,
    required,
    searchable,
    className
  } = t0;
  const placeholder = t1 === undefined ? "Select..." : t1;
  const onDark = t2 === undefined ? false : t2;
  const options = t3 === undefined ? [] : t3;
  const disabled = t4 === undefined ? false : t4;
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const listboxId = `${id}-listbox`;
  const descriptionId = `${id}-description`;
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [query, setQuery] = React.useState("");
  const internalRef = React.useRef(null);
  let t5;
  if ($[0] !== ref) {
    t5 = node => {
      (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }
    };
    $[0] = ref;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const containerRef = t5;
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const hasError = !!errorText;
  let t6;
  if ($[2] !== value) {
    t6 = o => o.value === value;
    $[2] = value;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const selectedOption = options.find(t6);
  const isPlaceholder = !selectedOption;
  const filterable = searchable ?? options.length > SEARCH_THRESHOLD;
  const q = query.trim().toLowerCase();
  const filteredOptions = !filterable || q === "" ? options : options.filter(o_0 => o_0.label.toLowerCase().includes(q));
  let t7;
  if ($[4] !== isOpen || $[5] !== onBlur || $[6] !== setIsOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      const handleClick = function handleClick(e) {
        if (internalRef.current && !internalRef.current.contains(e.target as Node)) {
          setIsOpen(false);
          onBlur?.();
        }
      };
      const handleFocusOut = function handleFocusOut(e_0) {
        if (internalRef.current && !internalRef.current.contains(e_0.relatedTarget as Node)) {
          setIsOpen(false);
          onBlur?.();
        }
      };
      document.addEventListener("mousedown", handleClick);
      internalRef.current?.addEventListener("focusout", handleFocusOut);
      const container = internalRef.current;
      return () => {
        document.removeEventListener("mousedown", handleClick);
        container?.removeEventListener("focusout", handleFocusOut);
      };
    };
    $[4] = isOpen;
    $[5] = onBlur;
    $[6] = setIsOpen;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== isOpen || $[9] !== onBlur) {
    t8 = [isOpen, onBlur];
    $[8] = isOpen;
    $[9] = onBlur;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  React.useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] !== highlightedIndex || $[12] !== isOpen) {
    t9 = () => {
      if (!isOpen || highlightedIndex < 0) {
        return;
      }
      const list = listRef.current;
      const item = list?.children[highlightedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({
        block: "nearest"
      });
    };
    t10 = [highlightedIndex, isOpen];
    $[11] = highlightedIndex;
    $[12] = isOpen;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[15] !== filterable || $[16] !== isOpen || $[17] !== setQuery) {
    t11 = () => {
      if (isOpen && filterable) {
        inputRef.current?.focus();
      } else {
        if (!isOpen) {
          setQuery("");
        }
      }
    };
    $[15] = filterable;
    $[16] = isOpen;
    $[17] = setQuery;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== filterable || $[20] !== isOpen) {
    t12 = [isOpen, filterable];
    $[19] = filterable;
    $[20] = isOpen;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  React.useEffect(t11, t12);
  let t13;
  if ($[22] !== disabled || $[23] !== isOpen || $[24] !== options || $[25] !== setHighlightedIndex || $[26] !== setIsOpen || $[27] !== value) {
    t13 = function open() {
      if (disabled || isOpen) {
        return;
      }
      const idx = options.findIndex(o_1 => o_1.value === value);
      setHighlightedIndex(idx >= 0 ? idx : 0);
      setIsOpen(true);
    };
    $[22] = disabled;
    $[23] = isOpen;
    $[24] = options;
    $[25] = setHighlightedIndex;
    $[26] = setIsOpen;
    $[27] = value;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const open = t13;
  let t14;
  if ($[29] !== disabled || $[30] !== isOpen || $[31] !== open || $[32] !== setIsOpen) {
    t14 = function toggle() {
      if (disabled) {
        return;
      }
      if (isOpen) {
        setIsOpen(false);
      } else {
        open();
      }
    };
    $[29] = disabled;
    $[30] = isOpen;
    $[31] = open;
    $[32] = setIsOpen;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const toggle = t14;
  let t15;
  if ($[34] !== onChange || $[35] !== setIsOpen) {
    t15 = function selectOption(optionValue) {
      onChange?.(optionValue);
      setIsOpen(false);
    };
    $[34] = onChange;
    $[35] = setIsOpen;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const selectOption = t15;
  let t16;
  if ($[37] !== filteredOptions || $[38] !== setHighlightedIndex) {
    t16 = function moveHighlight(direction) {
      setHighlightedIndex(i => {
        const n = filteredOptions.length;
        if (n === 0) {
          return -1;
        }
        if (direction === "down") {
          return i < n - 1 ? i + 1 : 0;
        }
        return i > 0 ? i - 1 : n - 1;
      });
    };
    $[37] = filteredOptions;
    $[38] = setHighlightedIndex;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const moveHighlight = t16;
  let t17;
  if ($[40] !== disabled || $[41] !== filterable || $[42] !== filteredOptions || $[43] !== highlightedIndex || $[44] !== isOpen || $[45] !== moveHighlight || $[46] !== open || $[47] !== selectOption || $[48] !== setIsOpen || $[49] !== toggle) {
    t17 = function handleKeyDown(e_1) {
      if (disabled) {
        return;
      }
      bb171: switch (e_1.key) {
        case "Enter":
          {
            e_1.preventDefault();
            if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
              selectOption(filteredOptions[highlightedIndex].value);
            } else {
              toggle();
            }
            break bb171;
          }
        case " ":
          {
            if (filterable && isOpen) {
              break bb171;
            }
            e_1.preventDefault();
            if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
              selectOption(filteredOptions[highlightedIndex].value);
            } else {
              toggle();
            }
            break bb171;
          }
        case "ArrowDown":
          {
            e_1.preventDefault();
            if (!isOpen) {
              open();
            } else {
              moveHighlight("down");
            }
            break bb171;
          }
        case "ArrowUp":
          {
            e_1.preventDefault();
            if (isOpen) {
              moveHighlight("up");
            }
            break bb171;
          }
        case "Escape":
          {
            e_1.preventDefault();
            setIsOpen(false);
          }
      }
    };
    $[40] = disabled;
    $[41] = filterable;
    $[42] = filteredOptions;
    $[43] = highlightedIndex;
    $[44] = isOpen;
    $[45] = moveHighlight;
    $[46] = open;
    $[47] = selectOption;
    $[48] = setIsOpen;
    $[49] = toggle;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  const handleKeyDown = t17;
  const activeDescendant = isOpen && filteredOptions[highlightedIndex] ? `${id}-option-${highlightedIndex}` : undefined;
  const t18 = hasError && "bg-input-surface-error hover:bg-input-surface-error focus:bg-input-surface-error";
  const t19 = disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled cursor-not-allowed";
  let chevron;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[51] !== activeDescendant || $[52] !== className || $[53] !== containerRef || $[54] !== descriptionId || $[55] !== disabled || $[56] !== filterable || $[57] !== handleKeyDown || $[58] !== hasError || $[59] !== helperText || $[60] !== id || $[61] !== isOpen || $[62] !== isPlaceholder || $[63] !== label || $[64] !== listboxId || $[65] !== name || $[66] !== onDark || $[67] !== open || $[68] !== placeholder || $[69] !== query || $[70] !== required || $[71] !== selectedOption?.label || $[72] !== setHighlightedIndex || $[73] !== setQuery || $[74] !== t18 || $[75] !== t19 || $[76] !== toggle || $[77] !== value) {
    const triggerBase = cn("flex w-full items-center justify-between cursor-pointer", "h-12 rounded-sm px-4", "typography-body-sm-regular", "outline-none", "border border-transparent", "bg-input-surface-rest", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover [.dark_&]:focus:border-transparent", t18, t19);
    const t27 = disabled ? "text-input-text-disabled" : "text-input-text-rest";
    let t28;
    if ($[86] !== t27) {
      t28 = cn("shrink-0", t27);
      $[86] = t27;
      $[87] = t28;
    } else {
      t28 = $[87];
    }
    let t29;
    if ($[88] !== isOpen) {
      t29 = isOpen ? <ChevronUp className="size-6" /> : <ChevronDown className="size-6" />;
      $[88] = isOpen;
      $[89] = t29;
    } else {
      t29 = $[89];
    }
    let t30;
    if ($[90] !== t28 || $[91] !== t29) {
      t30 = <span className={t28}>{t29}</span>;
      $[90] = t28;
      $[91] = t29;
      $[92] = t30;
    } else {
      t30 = $[92];
    }
    chevron = t30;
    t22 = containerRef;
    t23 = "select";
    const t31 = onDark && "dark";
    if ($[93] !== className || $[94] !== t31) {
      t24 = cn(t31, "flex w-full flex-col gap-2", className);
      $[93] = className;
      $[94] = t31;
      $[95] = t24;
    } else {
      t24 = $[95];
    }
    if ($[96] !== disabled || $[97] !== name || $[98] !== value) {
      t25 = name && <input type="hidden" name={name} value={value ?? ""} disabled={disabled} />;
      $[96] = disabled;
      $[97] = name;
      $[98] = value;
      $[99] = t25;
    } else {
      t25 = $[99];
    }
    const t32 = `${id}-label`;
    const t33 = disabled ? "text-input-text-disabled" : "text-input-text-label";
    let t34;
    if ($[100] !== t33) {
      t34 = cn("typography-component-sm-medium desktop:typography-component-md-medium", t33);
      $[100] = t33;
      $[101] = t34;
    } else {
      t34 = $[101];
    }
    let t35;
    if ($[102] !== required) {
      t35 = <RequiredMark required={required} />;
      $[102] = required;
      $[103] = t35;
    } else {
      t35 = $[103];
    }
    if ($[104] !== id || $[105] !== label || $[106] !== t32 || $[107] !== t34 || $[108] !== t35) {
      t26 = <label id={t32} htmlFor={id} className={t34}>{label}{t35}</label>;
      $[104] = id;
      $[105] = label;
      $[106] = t32;
      $[107] = t34;
      $[108] = t35;
      $[109] = t26;
    } else {
      t26 = $[109];
    }
    t20 = "relative";
    t21 = filterable ? <input ref={inputRef} id={id} type="text" role="combobox" autoComplete="off" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls={listboxId} aria-labelledby={`${id}-label`} aria-activedescendant={activeDescendant} aria-autocomplete="list" aria-invalid={hasError || undefined} aria-describedby={hasError || helperText ? descriptionId : undefined} disabled={disabled} readOnly={!isOpen} value={isOpen ? query : selectedOption?.label ?? ""} placeholder={isOpen ? selectedOption?.label ?? placeholder : placeholder} onClick={open} onChange={e_2 => {
      setQuery(e_2.target.value);
      setHighlightedIndex(0);
    }} onKeyDown={handleKeyDown} className={cn(triggerBase, "pr-12", isOpen ? "cursor-text" : "cursor-pointer", isPlaceholder && !isOpen ? "text-input-text-rest placeholder:text-input-text-disabled" : "text-input-text-rest")} /> : <button type="button" id={id} role="combobox" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls={listboxId} aria-labelledby={`${id}-label`} aria-activedescendant={activeDescendant} aria-invalid={hasError || undefined} aria-describedby={hasError || helperText ? descriptionId : undefined} disabled={disabled} onClick={toggle} onKeyDown={handleKeyDown} className={triggerBase}><span className={isPlaceholder ? "text-input-text-disabled" : "text-input-text-rest"}>{selectedOption?.label ?? placeholder}</span>{chevron}</button>;
    $[51] = activeDescendant;
    $[52] = className;
    $[53] = containerRef;
    $[54] = descriptionId;
    $[55] = disabled;
    $[56] = filterable;
    $[57] = handleKeyDown;
    $[58] = hasError;
    $[59] = helperText;
    $[60] = id;
    $[61] = isOpen;
    $[62] = isPlaceholder;
    $[63] = label;
    $[64] = listboxId;
    $[65] = name;
    $[66] = onDark;
    $[67] = open;
    $[68] = placeholder;
    $[69] = query;
    $[70] = required;
    $[71] = selectedOption?.label;
    $[72] = setHighlightedIndex;
    $[73] = setQuery;
    $[74] = t18;
    $[75] = t19;
    $[76] = toggle;
    $[77] = value;
    $[78] = chevron;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
  } else {
    chevron = $[78];
    t20 = $[79];
    t21 = $[80];
    t22 = $[81];
    t23 = $[82];
    t24 = $[83];
    t25 = $[84];
    t26 = $[85];
  }
  let t27;
  if ($[110] !== chevron || $[111] !== disabled || $[112] !== filterable || $[113] !== toggle) {
    t27 = filterable && <button type="button" tabIndex={-1} aria-hidden={true} disabled={disabled} onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2">{chevron}</button>;
    $[110] = chevron;
    $[111] = disabled;
    $[112] = filterable;
    $[113] = toggle;
    $[114] = t27;
  } else {
    t27 = $[114];
  }
  let t28;
  if ($[115] !== filterable || $[116] !== hasError || $[117] !== isOpen) {
    t28 = hasError && !isOpen && !filterable && <div className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 text-input-text-error"><Icon name="error" filled={true} size="lg" decorative={true} /></div>;
    $[115] = filterable;
    $[116] = hasError;
    $[117] = isOpen;
    $[118] = t28;
  } else {
    t28 = $[118];
  }
  let t29;
  if ($[119] !== filteredOptions || $[120] !== highlightedIndex || $[121] !== id || $[122] !== isOpen || $[123] !== listboxId || $[124] !== selectOption || $[125] !== setHighlightedIndex || $[126] !== value) {
    t29 = isOpen && <ul ref={listRef} id={listboxId} role="listbox" aria-labelledby={`${id}-label`} className="absolute top-[calc(100%+2px)] z-50 max-h-[320px] w-full overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-surface-base-primary">{filteredOptions.length === 0 ? <li className="px-3 py-4 typography-body-sm-regular text-input-text-disabled">No matches</li> : filteredOptions.map((option, index) => <li key={option.value} id={`${id}-option-${index}`} role="option" aria-selected={option.value === value} className={cn("cursor-pointer px-3 py-4", "typography-body-sm-regular text-text-primary", "hover:bg-input-surface-hover", highlightedIndex === index && "bg-input-surface-hover", option.value === value && "font-medium")} onMouseEnter={() => setHighlightedIndex(index)} onMouseDown={e_3 => {
        e_3.preventDefault();
        selectOption(option.value);
      }}>{option.label}</li>)}</ul>;
    $[119] = filteredOptions;
    $[120] = highlightedIndex;
    $[121] = id;
    $[122] = isOpen;
    $[123] = listboxId;
    $[124] = selectOption;
    $[125] = setHighlightedIndex;
    $[126] = value;
    $[127] = t29;
  } else {
    t29 = $[127];
  }
  let t30;
  if ($[128] !== t20 || $[129] !== t21 || $[130] !== t27 || $[131] !== t28 || $[132] !== t29) {
    t30 = <div className={t20}>{t21}{t27}{t28}{t29}</div>;
    $[128] = t20;
    $[129] = t21;
    $[130] = t27;
    $[131] = t28;
    $[132] = t29;
    $[133] = t30;
  } else {
    t30 = $[133];
  }
  let t31;
  if ($[134] !== descriptionId || $[135] !== errorText || $[136] !== hasError || $[137] !== helperText) {
    t31 = (hasError || helperText) && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{errorText || helperText}</p>;
    $[134] = descriptionId;
    $[135] = errorText;
    $[136] = hasError;
    $[137] = helperText;
    $[138] = t31;
  } else {
    t31 = $[138];
  }
  let t32;
  if ($[139] !== t22 || $[140] !== t23 || $[141] !== t24 || $[142] !== t25 || $[143] !== t26 || $[144] !== t30 || $[145] !== t31) {
    t32 = <div ref={t22} data-testid={t23} className={t24}>{t25}{t26}{t30}{t31}</div>;
    $[139] = t22;
    $[140] = t23;
    $[141] = t24;
    $[142] = t25;
    $[143] = t26;
    $[144] = t30;
    $[145] = t31;
    $[146] = t32;
  } else {
    t32 = $[146];
  }
  return t32;
});
Select.displayName = "Select";
export { Select };